import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import {
  BrandPlan,
  BrandPlanCrosspost,
  BrandPlanEvergreen,
  BrandPlanNoStrategy,
  BrandPlanPyramid,
  CrosspostStrategy,
  DeepPartial,
  PyramidStrategy,
} from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { sendToApi } from 'api/client'
import { AnchorContentType } from 'screens/BrandPlan/ChangeStrategyModal/UploadAnchorContentFile/Types'

interface Props {
  id: number
}

export type Data = BrandPlan

export interface CrosspostStrategyInput extends Omit<CrosspostStrategy, 'theme'> {
  theme: string
}

export interface PyramidStrategyInput extends Omit<PyramidStrategy, 'anchor_content'> {
  anchor_content_type: AnchorContentType
  anchor_content_id: number
}

interface BrandPlanCrosspostInput extends Omit<BrandPlanCrosspost, 'strategy'> {
  strategy: CrosspostStrategyInput
}

export type BrandPlanInput = BrandPlanNoStrategy | BrandPlanEvergreen | BrandPlanCrosspostInput | BrandPlanPyramid

export type Variables = DeepPartial<Omit<BrandPlanInput, 'id'>> & { also_update_schedules?: boolean }

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export const updateBrandPlan = async ({
  id,
  workspaceId,
  ...variables
}: Variables & Props & { workspaceId: number }) => {
  const response = await sendToApi<{ brand_plan: BrandPlan }>(`/w/${workspaceId}/brand_plans/${id}`, {
    method: 'PUT',
    body: variables,
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data.brand_plan
}

export const useUpdateBrandPlan = ({ id }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<Data, unknown, Variables>(
    'updateBrandPlan',
    async (variables) => {
      return updateBrandPlan({ id, workspaceId, ...variables })
    },
    options,
  )
}
